import { Trash2, X } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ClientStatus } from "@/features/clients/types";

interface BulkActionsBarProps {
  selectedCount: number;
  busy: boolean;
  onSetStatus: (status: ClientStatus) => void;
  onDelete: () => void;
  onClear: () => void;
}

type PendingAction = { kind: "delete" } | { kind: "set_status"; status: ClientStatus };

const statusLabels: Record<ClientStatus, string> = {
  active: "Active",
  onboarding: "Onboarding",
  inactive: "Inactive",
};

/**
 * Toolbar shown while list rows are selected. Both mutations sit behind a
 * confirm dialog; the parent owns the API calls and selection state.
 */
export function BulkActionsBar({
  selectedCount,
  busy,
  onSetStatus,
  onDelete,
  onClear,
}: BulkActionsBarProps) {
  const [status, setStatus] = useState<ClientStatus | "">("");
  const [pending, setPending] = useState<PendingAction | null>(null);

  if (selectedCount === 0) return null;

  function confirmPending() {
    if (!pending) return;
    if (pending.kind === "delete") onDelete();
    else onSetStatus(pending.status);
    setPending(null);
  }

  return (
    <div className="bg-muted/50 flex flex-wrap items-center gap-3 border-b px-4 py-3">
      <span className="text-foreground text-sm font-medium">{selectedCount} selected</span>
      <div className="flex items-center gap-2">
        <Select value={status} onValueChange={(v) => setStatus(v as ClientStatus)}>
          <SelectTrigger size="sm" className="w-[150px]" disabled={busy} aria-label="Set status">
            <SelectValue placeholder="Set status to…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || !status}
          onClick={() => status && setPending({ kind: "set_status", status })}
        >
          Apply
        </Button>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="text-destructive"
        disabled={busy}
        onClick={() => setPending({ kind: "delete" })}
      >
        <Trash2 /> Delete
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} onClick={onClear}>
        <X /> Clear
      </Button>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === "delete"
                ? `Delete ${selectedCount} client${selectedCount === 1 ? "" : "s"}?`
                : `Set ${selectedCount} client${selectedCount === 1 ? "" : "s"} to ${
                    pending ? statusLabels[pending.status] : ""
                  }?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.kind === "delete"
                ? "The selected clients will be removed from your lists. Records tied to past filings are retained and can be restored by support."
                : "The status change applies immediately to every selected client."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPending}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
